import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Search, ExternalLink, Award, CheckCircle2, Filter, Send, Info, AlertCircle, BookOpen, Coins, Briefcase, HeartHandshake, HardHat, Sprout, ChefHat, Palette, Laptop, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { FadeIn } from "../components/motion";

const CATS = [
  { k: "all", label: "All", icon: BookOpen },
  { k: "basic_education", label: "Basic education", icon: BookOpen },
  { k: "digital_technology", label: "Digital & tech", icon: Laptop },
  { k: "entrepreneurship", label: "Entrepreneurship", icon: Briefcase },
  { k: "financial_education", label: "Financial", icon: Coins },
  { k: "employment_career", label: "Career", icon: GraduationCap },
  { k: "vocational_construction", label: "Construction", icon: HardHat },
  { k: "vocational_agriculture", label: "Agriculture", icon: Sprout },
  { k: "vocational_fashion", label: "Fashion & creative", icon: Palette },
  { k: "vocational_hospitality", label: "Hospitality", icon: ChefHat },
  { k: "spiritual", label: "Spiritual", icon: HeartHandshake },
  { k: "foundation_original", label: "DKF originals", icon: Sparkles },
];

const PHILOSOPHY = [
  "Empowerment is more than giving money.",
  "Knowledge can create opportunity.",
  "Skills can create income.",
  "Financial education can protect income.",
  "Character can sustain progress.",
  "Faith can provide hope.",
];

export default function Learn() {
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [freeOnly, setFreeOnly] = useState(true);
  const [certOnly, setCertOnly] = useState(false);
  const [resources, setResources] = useState([]);
  const [opps, setOpps] = useState([]);
  const [stats, setStats] = useState({});
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggest, setSuggest] = useState({ resource_name: "", provider: "", url: "", category: "digital_technology", is_free: true, country: "Global", description: "", submitted_by: "", email: "" });
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    document.title = "Learning & Skills Hub | Dutsen Kauna Foundation";
    api.get("/learning/resources").then((r) => setResources(r.data)).catch(() => {});
    api.get("/learning/opportunities").then((r) => setOpps(r.data)).catch(() => {});
    api.get("/learning/impact").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => resources.filter((r) => {
    if (cat !== "all" && r.category !== cat) return false;
    if (freeOnly && !r.is_free) return false;
    if (certOnly && !r.has_certificate) return false;
    if (q) {
      const s = `${r.title} ${r.provider} ${r.description}`.toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [resources, cat, q, freeOnly, certOnly]);

  const trackClick = (id) => api.post(`/learning/resources/${id}/click`).catch(() => {});

  const submitSuggest = async (e) => {
    e.preventDefault();
    setSuggesting(true);
    try {
      await api.post("/learning/suggest", suggest);
      toast.success("Thank you — our team will review and, if approved, publish it soon.");
      setSuggestOpen(false);
      setSuggest({ resource_name: "", provider: "", url: "", category: "digital_technology", is_free: true, country: "Global", description: "", submitted_by: "", email: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSuggesting(false); }
  };

  return (
    <div data-testid="learn-page">
      {/* Hero */}
      <section className="relative bg-[#155e3f] text-white py-20 md:py-28 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1509062522246-3755977927d7?w=1600" alt="Young people learning digital skills at a community learning hub" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] via-[#155e3f]/85 to-[#155e3f]/40" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.35em] text-xs font-semibold uppercase">Learning & Skills Hub</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-3 max-w-4xl leading-[1.05]">Learn. Grow. Build. <span className="italic text-[#d6aa43]">Serve.</span></h1>
          <p className="mt-6 text-white text-lg max-w-3xl">
            Free learning, skills development, financial education and spiritual encouragement — from Dutsen Kauna Foundation and trusted global partners. Eligibility for humanitarian support is based on need and programme criteria only, never on engagement with spiritual materials.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#resources" className="dk-btn-primary" data-testid="learn-start">Start learning</a>
            <a href="#opportunities" className="dk-btn-outline">Free training near you</a>
            <button onClick={() => setSuggestOpen(true)} className="text-sm text-[#d6aa43] hover:underline self-center" data-testid="recommend-btn">Recommend a resource →</button>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            {[
              ["Resources", stats.resources],
              ["Opportunities", stats.opportunities],
              ["Training partners", stats.training_partners],
              ["People referred", stats.referrals],
            ].map(([l, v]) => (
              <div key={l} className="p-4 rounded-xl border border-white/10 bg-white/[0.03]">
                <div className="font-serif text-2xl md:text-3xl font-bold text-[#d6aa43]">{v ?? "—"}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories & filters */}
      <section id="resources" className="py-16 bg-white">
        <div className="dk-container">
          <FadeIn>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
              <div>
                <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Choose what to learn</span>
                <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">Free courses & partners.</h2>
              </div>
              <div className="text-sm text-[#0a0d12]/70">{filtered.length} of {resources.length} resources</div>
            </div>
          </FadeIn>

          <div className="grid lg:grid-cols-[240px,1fr] gap-8">
            {/* Left rail — categories */}
            <aside>
              <div className="flex flex-col gap-1 sticky top-24" data-testid="learn-cats">
                {CATS.map((c) => {
                  const Icon = c.icon;
                  const active = cat === c.k;
                  return (
                    <button
                      key={c.k}
                      onClick={() => setCat(c.k)}
                      data-testid={`cat-${c.k}`}
                      className={`text-left px-3 py-2 rounded-md flex items-center gap-2 text-sm transition ${active ? "bg-[#155e3f] text-[#d6aa43]" : "text-[#0a0d12] hover:bg-[#f8f4e8]"}`}
                    >
                      <Icon size={14} /> {c.label}
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* Content */}
            <div>
              <div className="flex flex-wrap gap-3 items-center mb-6">
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0a0d12]/40" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, provider or topic…" className="w-full pl-9 pr-3 py-2.5 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid="learn-search" />
                </div>
                <label className="text-sm flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={freeOnly} onChange={(e) => setFreeOnly(e.target.checked)} /> Free only
                </label>
                <label className="text-sm flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={certOnly} onChange={(e) => setCertOnly(e.target.checked)} /> With certificate
                </label>
              </div>

              <div className="grid md:grid-cols-2 gap-4" data-testid="learn-resources">
                {filtered.map((r) => (
                  <article key={r.id} className="group rounded-xl overflow-hidden bg-[#f8f4e8] border border-black/5 hover:-translate-y-1 hover:shadow-lg transition" data-testid={`resource-${r.id}`}>
                    <div className="relative h-40 overflow-hidden">
                      <img src={r.image_url || "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=800"} alt="Young people learning digital skills at a community learning hub" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                      <div className="absolute top-2 right-2 flex gap-1">
                        {r.verified && <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-[#d6aa43] text-[#0a0d12] rounded"><CheckCircle2 size={10} className="inline" /> DKF</span>}
                        {r.is_free && <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-green-500 text-white rounded">Free</span>}
                        {r.has_certificate && <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-[#155e3f] text-white rounded"><Award size={10} className="inline" /> Cert</span>}
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#d6aa43] font-bold">{r.provider}</div>
                      <h3 className="font-serif text-lg text-[#0a0d12] font-bold mt-1">{r.title}</h3>
                      <p className="text-sm text-[#0a0d12]/70 mt-2 line-clamp-3">{r.description}</p>
                      <a href={r.url.startsWith("/") ? r.url : r.url} target={r.url.startsWith("/") ? "_self" : "_blank"} rel="noreferrer" onClick={() => trackClick(r.id)} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0d1724] hover:text-[#d6aa43]">
                        Start learning <ExternalLink size={14} />
                      </a>
                    </div>
                  </article>
                ))}
                {filtered.length === 0 && (
                  <div className="col-span-full p-10 rounded-xl border border-dashed border-[#d6aa43]/40 text-center text-[#0a0d12]/60">
                    No resources match. Try clearing filters or a different category.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Opportunities */}
      <section id="opportunities" className="py-16 bg-[#f8f4e8]">
        <div className="dk-container">
          <FadeIn>
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Training opportunities</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3 mb-8">Places open right now.</h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-5" data-testid="learn-opps">
            {opps.map((o) => (
              <div key={o.id} className="bg-white rounded-xl border border-black/5 overflow-hidden">
                <div className="h-40 overflow-hidden">
                  <img src={o.image_url || "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800"} alt="Young people learning digital skills at a community learning hub" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#d6aa43] font-bold">{o.provider}</div>
                  <h3 className="font-serif text-lg text-[#0a0d12] font-bold mt-1">{o.title}</h3>
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                    <Chip>{o.location || (o.is_online ? "Online" : "TBC")}</Chip>
                    <Chip>{o.cost || "Free"}</Chip>
                    <Chip>{o.duration || "TBC"}</Chip>
                    {o.has_certificate && <Chip>Certificate</Chip>}
                    {o.places_available > 0 && <Chip>{o.places_available} places</Chip>}
                  </div>
                  <p className="text-sm text-[#0a0d12]/75 mt-3 line-clamp-2">{o.description}</p>
                  <a href={o.apply_url || "/apply"} target={o.apply_url?.startsWith("http") ? "_blank" : "_self"} rel="noreferrer" className="dk-btn-primary !py-2 !px-4 text-sm mt-4">Apply <ExternalLink size={12} /></a>
                </div>
              </div>
            ))}
            {opps.length === 0 && <p className="text-[#0a0d12]/60">No open opportunities right now.</p>}
          </div>
        </div>
      </section>

      {/* Empowerment link */}
      <section className="py-16 bg-[#155e3f] text-white">
        <div className="dk-container grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Learn → Empowerment</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3">Don't just give capital. Help people learn how to use it.</h2>
            <p className="mt-4 text-white">Empowerment applicants are encouraged to complete relevant business-learning modules before or after receiving support. Sustainable transformation follows the path: <strong className="text-white">Training → Mentoring → Empowerment → Monitoring → Growth</strong>.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/apply?category=empowerment" className="dk-btn-primary">Apply for empowerment</Link>
              <Link to="/empowerment" className="dk-btn-outline">See the programme</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {["Training", "Mentoring", "Empowerment", "Growth"].map((step, i) => (
              <div key={step} className="p-6 rounded-xl bg-white/[0.05] border border-white/10">
                <div className="text-[#d6aa43] font-bold text-xs tracking-[0.25em]">0{i + 1}</div>
                <div className="font-serif text-2xl font-bold mt-2">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy + fraud */}
      <section className="py-16 bg-white">
        <div className="dk-container grid lg:grid-cols-2 gap-10">
          <div className="p-8 rounded-2xl border border-[#d6aa43]/20 bg-[#f8f4e8]">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Our philosophy</span>
            <ul className="mt-5 space-y-2">
              {PHILOSOPHY.map((p) => (
                <li key={p} className="flex gap-2 text-[#0a0d12]"><CheckCircle2 size={16} className="text-[#d6aa43] flex-shrink-0 mt-1" /> <span>{p}</span></li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-2xl border-l-4 border-red-500 bg-red-50">
            <div className="flex items-center gap-2 mb-3"><AlertCircle className="text-red-600" /> <h3 className="font-serif text-xl font-bold text-[#0a0d12]">Stay safe while learning online</h3></div>
            <ul className="text-sm text-[#0a0d12]/80 space-y-1.5">
              <li>• Never send money to an individual's account to access a course advertised as free.</li>
              <li>• Be cautious of fake scholarships, guaranteed-income schemes and fake investment courses.</li>
              <li>• Verify the provider's current terms before enrolling.</li>
              <li>• DKF never asks for your password or PIN. Report suspicious requests.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Suggest modal */}
      {suggestOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSuggestOpen(false)}>
          <form onSubmit={submitSuggest} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-7 max-w-xl w-full max-h-[92vh] overflow-y-auto" data-testid="suggest-form">
            <h3 className="font-serif text-2xl font-bold text-[#0a0d12] mb-1">Recommend a free resource</h3>
            <p className="text-sm text-[#0a0d12]/70 mb-5">Submissions are reviewed before publication.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Resource name" required v={suggest.resource_name} on={(v) => setSuggest({ ...suggest, resource_name: v })} tid="sg-name" />
              <F label="Provider" required v={suggest.provider} on={(v) => setSuggest({ ...suggest, provider: v })} tid="sg-provider" />
              <F label="URL (https://…)" required v={suggest.url} on={(v) => setSuggest({ ...suggest, url: v })} className="sm:col-span-2" tid="sg-url" />
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Category</label>
                <select value={suggest.category} onChange={(e) => setSuggest({ ...suggest, category: e.target.value })} className="w-full px-4 py-2.5 border border-black/15 rounded-md">
                  {CATS.filter((c) => c.k !== "all").map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
                </select>
              </div>
              <F label="Country" v={suggest.country} on={(v) => setSuggest({ ...suggest, country: v })} />
              <F label="Your name" v={suggest.submitted_by} on={(v) => setSuggest({ ...suggest, submitted_by: v })} />
              <F label="Your email" type="email" required v={suggest.email} on={(v) => setSuggest({ ...suggest, email: v })} tid="sg-email" />
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Description</label>
                <textarea rows={3} value={suggest.description} onChange={(e) => setSuggest({ ...suggest, description: e.target.value })} className="w-full px-4 py-3 border border-black/15 rounded-md" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setSuggestOpen(false)} className="px-4 py-2 text-sm">Cancel</button>
              <button type="submit" disabled={suggesting} className="dk-btn-primary" data-testid="sg-submit">{suggesting ? "Submitting…" : <>Submit <Send size={14} /></>}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const Chip = ({ children }) => <span className="px-2 py-0.5 rounded bg-[#f8f4e8] text-[#0a0d12] text-[10px] uppercase tracking-wide font-bold">{children}</span>;

function F({ label, v, on, type = "text", required, className = "", tid }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">{label}{required ? " *" : ""}</label>
      <input required={required} type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full px-4 py-2.5 border border-black/15 rounded-md" data-testid={tid} />
    </div>
  );
}
